/**
 * Family Member Calendars Screen
 *
 * Full-page calendar management for a family member.
 * Replaces the calendar mapping modal with a dedicated screen.
 */

import { useEffect, useState, useCallback } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import Animated, { FadeIn } from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing } from '@/constants/spacing';
import { getBodyFont, getHeadingFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { useFamilyMember } from '@/hooks/use-family-member';
import { useMemberCalendarMapping } from '@/hooks/use-member-calendar-mapping';
import { deviceCalendarService, type DeviceCalendar } from '@/lib/calendar/DeviceCalendarService';
import { CalendarToggleItem } from '@/components/family/CalendarToggleItem';
import { CalendarLinkPrompt } from '@/components/family/CalendarLinkPrompt';

export default function FamilyMemberCalendarsScreen() {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const { member, isLoading: isLoadingMember } = useFamilyMember(id ?? null);

  const [calendars, setCalendars] = useState<DeviceCalendar[]>([]);
  const [isLoadingCalendars, setIsLoadingCalendars] = useState(true);
  const [togglingCalendarId, setTogglingCalendarId] = useState<string | null>(null);
  const [linkPromptCalendarName, setLinkPromptCalendarName] = useState<string | null>(null);

  const {
    isCalendarLinked,
    toggleCalendar,
  } = useMemberCalendarMapping(id ?? null);

  const handleBack = useCallback(() => {
    router.back();
  }, [router]);

  // Load device calendars on mount
  useEffect(() => {
    loadCalendars();
  }, []);

  const loadCalendars = async () => {
    try {
      setIsLoadingCalendars(true);
      const deviceCalendars = await deviceCalendarService.getDeviceCalendars();
      setCalendars(deviceCalendars);
    } catch (error) {
      console.error('Error loading device calendars:', error);
    } finally {
      setIsLoadingCalendars(false);
    }
  };

  const handleToggle = useCallback(async (calendarId: string) => {
    const wasLinked = isCalendarLinked(calendarId);
    setTogglingCalendarId(calendarId);
    try {
      await toggleCalendar(calendarId);
      // Show prompt when newly linking (not unlinking)
      if (!wasLinked) {
        const cal = calendars.find((c) => c.id === calendarId);
        if (cal) {
          setLinkPromptCalendarName(cal.title);
        }
      }
    } catch (error) {
      console.error('Error toggling calendar mapping:', error);
    } finally {
      setTogglingCalendarId(null);
    }
  }, [toggleCalendar, isCalendarLinked, calendars]);

  const handleShowCalendars = useCallback(() => {
    setLinkPromptCalendarName(null);
    router.navigate('/(tabs)/calendar?openDrawer=true' as any);
  }, [router]);

  const handleDismissPrompt = useCallback(() => {
    setLinkPromptCalendarName(null);
  }, []);

  const isLoading = isLoadingMember || isLoadingCalendars;

  if (isLoading) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['top']}>
        <View style={[styles.header, { borderBottomColor: palette.borderMuted }]}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Ionicons name="arrow-back" size={24} color={palette.primary} />
          </TouchableOpacity>
          <Text style={[styles.headerTitle, { color: palette.text, fontFamily: getHeadingFont() }]}>
            Calendars
          </Text>
          <View style={styles.headerSpacer} />
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={palette.primary} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: palette.background }]} edges={['top']}>
      {/* Header */}
      <View style={[styles.header, { borderBottomColor: palette.borderMuted }]}>
        <TouchableOpacity onPress={handleBack} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={palette.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: palette.text, fontFamily: getHeadingFont() }]}>
          Calendars
        </Text>
        <View style={styles.headerSpacer} />
      </View>

      {/* Description */}
      <Animated.View entering={FadeIn.duration(300)} style={styles.descriptionContainer}>
        <Text style={[styles.description, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
          Events from linked calendars will be automatically assigned to {member?.name || 'this member'}.
        </Text>
      </Animated.View>

      {/* Calendar List */}
      {calendars.length === 0 ? (
        <Animated.View entering={FadeIn.duration(300).delay(100)} style={styles.emptyContainer}>
          <Ionicons name="calendar-outline" size={48} color={palette.textMuted} />
          <Text style={[styles.emptyText, { color: palette.textMuted, fontFamily: getBodyFont() }]}>
            No calendars found
          </Text>
        </Animated.View>
      ) : (
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollContent}
        >
          <Animated.View entering={FadeIn.duration(300).delay(100)}>
            {calendars.map((calendar, index) => (
              <View key={calendar.id}>
                {index > 0 && (
                  <View style={[styles.divider, { backgroundColor: palette.borderMuted }]} />
                )}
                <CalendarToggleItem
                  calendar={calendar}
                  isLinked={isCalendarLinked(calendar.id)}
                  onToggle={handleToggle}
                  disabled={togglingCalendarId !== null}
                />
              </View>
            ))}
          </Animated.View>
        </ScrollView>
      )}

      <CalendarLinkPrompt
        visible={linkPromptCalendarName !== null}
        calendarName={linkPromptCalendarName ?? ''}
        onShowCalendars={handleShowCalendars}
        onDismiss={handleDismissPrompt}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  backButton: {
    padding: Spacing.xs,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  headerSpacer: {
    width: 32,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  descriptionContainer: {
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.lg,
  },
  description: {
    fontSize: 14,
    textAlign: 'center',
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: Spacing.md,
    padding: 48, // intentionally large for visual centering
  },
  emptyText: {
    fontSize: 16,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 48, // intentionally large for scroll clearance
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginLeft: 44, // align with toggle content
  },
});
